import {
  ApplicationConfig,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';

/*
 * Datos de localizacion del espanol. Esto estaba en app.module.ts:10 del proyecto
 * original como una llamada suelta a nivel de modulo, no como un proveedor, y al
 * convertir el NgModule a app.config se me quedo fuera.
 *
 * Sin estos datos, `formatDate` no encuentra el locale y DatePipe.transform lanza
 * NG02100 (InvalidPipeArgument). Cinco componentes instancian `new DatePipe(...)` a mano
 * y lo llaman dentro de un forEach sobre los resultados, asi que la excepcion aborta el
 * callback a medias: el arreglo de filas queda a medio llenar y `page.totalElements`
 * nunca se asigna. De ahi sale el segundo error, el `RangeError: Invalid array length`
 * de ngx-datatable, que hace `length = totalPages` con un NaN.
 *
 * Es decir: una sola causa, dos errores encadenados y ninguno de los dos apuntando al
 * sitio real.
 *
 * Nota: el proyecto escribe el locale de tres formas (`es-MX`, `es-Mx`, `es`). Angular
 * normaliza a minusculas y cae al idioma padre, asi que registrar `es` cubre las tres.
 */
registerLocaleData(localeEs);
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CookieService } from 'ngx-cookie-service';
import { routes } from './app.routes';
import { ZeroService } from './_services/zero/zero.service';
import { Globals } from './_services/zero/globals';
import { CatalogosService } from './_services/catalogos.service';
import { AlertasService } from './_services/alertas.service';
import { CamposService } from './_services/campos.service';
import { SessionService } from './_services/session.service';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { LoginProvider } from './_helpers/login-interceptor';
import { HttpConfigInterceptor } from './interceptor/interceptor';

/*
 * Lo que antes vivia en el bloque providers del NgModule unico del proyecto. Se mueve
 * aqui una sola vez para todo el proyecto. withInterceptorsFromDi() es lo que mantiene
 * funcionando los interceptores de clase sin reescribirlos como funciones.
 *
 * Sobre los interceptores, porque los documentos de analisis lo tenian mal: el proyecto
 * original declara 4 clases que implementan HttpInterceptor y registra 3.
 *
 *   ErrorInterceptor       si, explicito
 *   LoginInterceptor       si, via LoginProvider, que es el que lo declara multi:true
 *   HttpConfigInterceptor  si, explicito
 *   JwtInterceptor         NO, esta comentado en app.module.ts:184
 *
 * Se conserva el orden exacto del arreglo original, porque Angular los aplica en el
 * orden en que se proveen y HttpConfigInterceptor es el que valida la cookie.
 *
 * Nota para revisar aparte: LoginInterceptor es un backend simulado que responde
 * /users/authenticate con roles de prueba. Se porta tal cual por paridad funcional, pero
 * conviene evaluar si debe ir en el build de produccion.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    /*
     * Deteccion de cambios POR ZONA, declarada explicitamente.
     *
     * Faltaba, y es la causa de que los combos salieran vacios aunque el backend
     * respondiera bien: `esquemas = response.response` se asigna dentro del callback de
     * un XHR, y sin NgZone nadie avisa a Angular de que hay que repintar. El dato llega,
     * la vista no se actualiza, y no hay ningun error.
     *
     * Tener zone.js en los polyfills de angular.json NO basta: eso solo carga la
     * libreria. Hay que declarar tambien el proveedor. Yo habia hecho solo la primera
     * mitad, y asi el proyecto quedaba en modo zoneless, donde solo repintan las senales,
     * los eventos de plantilla y el pipe async: una asignacion suelta en un callback no.
     *
     * Afecta a TODO el proyecto, no solo al login: practicamente cada pantalla llena sus
     * datos con este mismo patron.
     *
     * Zoneless es la direccion correcta a futuro, pero exige revisar las ~45 suscripciones
     * del proyecto una por una. Es la decision que la seccion 5.3 del plan decia tomar
     * temprano: se queda con zona para el port, y se evalua despues con pruebas puestas.
     */
    provideZoneChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'es-MX' },
    // ngx-translate 18 ya no usa TranslateModule.forRoot sino este proveedor.
    provideTranslateService({ fallbackLang: 'es' }),
    // ngx-toastr y sus animaciones: antes entraban por ToastrModule.forRoot().
    provideAnimations(),
    provideToastr(),
    ZeroService,
    Globals,
    // El NgModule unico listaba estos cuatro a mano porque ninguno declara
    // providedIn: 'root'; CamposService ni siquiera lleva @Injectable. Se registran aqui
    // de una vez para todo el proyecto, en vez de ir descubriendolos pantalla por
    // pantalla cuando falla la inyeccion en tiempo de ejecucion.
    CatalogosService,
    AlertasService,
    CamposService,
    SessionService,
    // Los 3 interceptores, en el orden del app.module.ts original.
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    LoginProvider,
    { provide: HTTP_INTERCEPTORS, useClass: HttpConfigInterceptor, multi: true },
    // Lo inyectan HttpConfigInterceptor y 7 archivos mas: es la cookie BUB.
    CookieService,
  ],
};
